import SmartphoneIcon from "@mui/icons-material/Smartphone";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { ColumnDef } from "@tanstack/react-table";

import StatusLight from "@/components/StatusLight";
import type { DriverRow } from "@/types/driver";
import ColumnHeader from "@/ui/ColumnHeader";
import type { DataTableFeatures } from "@/ui/dataTableFeatures";
import type { ColumnFilterOptions } from ".";
import { columnWidthFloors } from "./columnMinWidth";
import DriverAvailabilityLegend, { PERSONAL_DEVICE_TOOLTIP } from "./DriverAvailabilityLegend";
import { formatHandsFreeAvailability } from "./handsFreeAvailability";
import { TOOLTIP_ENTER_DELAY } from "./tooltipDelay";

/**
 * Hands-free status and personal-device indicator, each rendered only when it has something to say.
 *
 * Status precedence (never-logged-in → offline → work-device) lives in `formatHandsFreeAvailability`.
 * The personal-device glyph is independent and can sit beside any of those.
 */
function DriverAvailabilityCell({ driver, neverLoggedIn }: { driver: DriverRow; neverLoggedIn: boolean }) {
  const status = formatHandsFreeAvailability(driver, { neverLoggedIn });

  if (!status && !driver.hasPersonalDevice) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {status && (
        <Tooltip
          title={status.tooltip}
          enterDelay={TOOLTIP_ENTER_DELAY.toMillis()}
          enterNextDelay={TOOLTIP_ENTER_DELAY.toMillis()}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StatusLight color={status.color} size={10} />
            <Box>{status.label}</Box>
          </Box>
        </Tooltip>
      )}
      {driver.hasPersonalDevice && (
        <Tooltip
          title={PERSONAL_DEVICE_TOOLTIP}
          enterDelay={TOOLTIP_ENTER_DELAY.toMillis()}
          enterNextDelay={TOOLTIP_ENTER_DELAY.toMillis()}
        >
          {/* SvgIcon marks itself aria-hidden by default; here the icon is the only carrier of the fact. */}
          <SmartphoneIcon
            role="img"
            aria-label={PERSONAL_DEVICE_TOOLTIP}
            aria-hidden={false}
            sx={{ fontSize: 16, flexShrink: 0 }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

const STATUS_COLUMN_TITLE = "Driver Availability";

export function statusColumn(options: ColumnFilterOptions): ColumnDef<DataTableFeatures, DriverRow> {
  const { minSize, initialMinWidth } = columnWidthFloors(STATUS_COLUMN_TITLE, { draggable: true, hasFilter: true });
  const unactivatedMembershipIds = options.unactivatedMembershipIds;
  return {
    accessorFn: (row) => row.calculatedAvailability ?? "",
    id: "status",
    header: () => (
      <ColumnHeader
        title={STATUS_COLUMN_TITLE}
        titleTooltip={<DriverAvailabilityLegend />}
        extra={options.getColumnExtra("status")}
        isActive={options.isFilterActive("status")}
      />
    ),
    cell: ({ row }) => (
      <DriverAvailabilityCell
        driver={row.original}
        neverLoggedIn={unactivatedMembershipIds?.has(row.original.workspaceMembershipId) ?? false}
      />
    ),
    meta: { flex: 2, draggable: true, initialMinWidth },
    minSize,
    enableSorting: false,
    enableGlobalFilter: false,
  };
}
